import React, { useMemo, useState, useRef } from 'react';
import { ComposableMap, Geographies, Geography } from 'react-simple-maps';
import { geoEqualEarth, geoPath } from 'd3-geo';
import { forceSimulation, forceX, forceY, forceCollide } from 'd3-force';
import { feature as topoFeature } from 'topojson-client';
import worldTopo from 'world-atlas/countries-110m.json';
import { getCountryName } from './countryUtils';
import { countryPopulations } from './countryPopulations';
import { countryIsoNumeric } from './countryIsoNumeric';

// Sequential multi-hue ramp (yellow-green -> teal -> blue -> navy), low ->
// high, used for the log-scaled bins over positive values.
const POSITIVE_RAMP = ['#c7e9b4', '#7fcdbb', '#41b6c4', '#2c7fb8', '#253494'];
// A country with no fighters in the current top-N is real data, not a gap.
const ZERO_COLOR = '#ffffcc';
// The full ordered set of bin colors, indexed 0 (zero) .. POSITIVE_RAMP.length.
const BIN_COLORS = [ZERO_COLOR, ...POSITIVE_RAMP];
// Reserved for map features we can't match to any of our country codes at all
// (a real gap in the geography lookup, not a "zero fighters" data point).
const UNMAPPED_COLOR = '#333';
const DIMMED_OPACITY = 0.15;

function hexToRgbArr(hex) {
  const h = hex.replace('#', '');
  return [parseInt(h.substring(0, 2), 16), parseInt(h.substring(2, 4), 16), parseInt(h.substring(4, 6), 16)];
}

// Smooth color for any t in [0,1] by linearly interpolating between the two
// nearest stops in BIN_COLORS (a continuous gradient, not a 6-color snap).
function interpolateRamp(stops, t) {
  const n = stops.length;
  const scaled = Math.min(Math.max(t, 0), 1) * (n - 1);
  const i0 = Math.floor(scaled);
  const i1 = Math.min(i0 + 1, n - 1);
  const localT = scaled - i0;
  const a = hexToRgbArr(stops[i0]);
  const b = hexToRgbArr(stops[i1]);
  const r = Math.round(a[0] + (b[0] - a[0]) * localT);
  const g = Math.round(a[1] + (b[1] - a[1]) * localT);
  const bl = Math.round(a[2] + (b[2] - a[2]) * localT);
  return `rgb(${r}, ${g}, ${bl})`;
}

const MAP_WIDTH = 800;
const MAP_HEIGHT = 480;
const MAP_SCALE = 180;

const RU_CODES = ['RU', 'RU-CE', 'RU-DA', 'RU-IN', 'RU-SE', 'RU-KB', 'RU-KC'];

const numericToAlpha2 = Object.fromEntries(
  Object.entries(countryIsoNumeric).map(([alpha2, numeric]) => [numeric, alpha2])
);

// Same projection/scale/translate as the ComposableMap below, used to compute
// country centroids for the Dorling cartogram so its circles line up with
// the choropleth's geography.
const dorlingProjection = geoEqualEarth().scale(MAP_SCALE).translate([MAP_WIDTH / 2, MAP_HEIGHT / 2]);
const dorlingPath = geoPath(dorlingProjection);
const worldFeatures = topoFeature(worldTopo, worldTopo.objects.countries).features;

const MAX_RADIUS = 28;

// Some countries' geometry is a MultiPolygon that bundles far-flung overseas
// territories with the mainland (e.g. France + French Guiana, US + Alaska
// pulling less here but still). The naive centroid of the whole MultiPolygon
// is area-weighted across every piece, which can land the circle in open
// ocean between two continents. Use only the largest (by projected area)
// piece instead, so the circle sits on the actual main landmass.
function getMainLandmassCentroid(geometry) {
  if (geometry.type !== 'MultiPolygon') return dorlingPath.centroid(geometry);
  let largestRing = null;
  let largestArea = -Infinity;
  geometry.coordinates.forEach(polygonCoords => {
    const polygon = { type: 'Polygon', coordinates: polygonCoords };
    const area = Math.abs(dorlingPath.area(polygon));
    if (area > largestArea) {
      largestArea = area;
      largestRing = polygon;
    }
  });
  return largestRing ? dorlingPath.centroid(largestRing) : dorlingPath.centroid(geometry);
}

function WorldRankingsMap({ rankings, onSelectCountry, filterSummary }) {
  const [metric, setMetric] = useState('perMillion'); // 'fighters' | 'perMillion'
  const [hovered, setHovered] = useState(null); // { code, name }
  const [cursorPos, setCursorPos] = useState({ x: 0, y: 0 });
  const [hoveredBin, setHoveredBin] = useState(null); // -1 (no data) | 0..POSITIVE_RAMP.length | null
  const containerRef = useRef(null);

  // Aggregate raw rankings into one fighter count per real, mappable country code.
  const fighterCounts = useMemo(() => {
    const counts = {};
    (rankings || []).forEach(row => {
      if (row.country_code === 'RU-WHOLE' || row.country_code === 'EU-NORU') return;
      const code = RU_CODES.includes(row.country_code)
        ? 'RU'
        : row.country_code === 'UK' ? 'GB' : row.country_code;
      counts[code] = (counts[code] || 0) + row.fighter_count;
    });
    return counts;
  }, [rankings]);

  // The metric actually driving the map's coloring/binning/legend. Every
  // mappable country gets a value (0 if it has no fighters in the current
  // top-N) - absence of fighters is real data, not a missing value.
  const countryValues = useMemo(() => {
    const values = {};
    Object.keys(numericToAlpha2).forEach(numeric => {
      const code = numericToAlpha2[numeric];
      const fighterCount = fighterCounts[code] || 0;
      if (metric === 'perMillion') {
        // Unlike the table above, the map doesn't blank out countries with a
        // single fighter - it always shows the real per-million value.
        const pop = countryPopulations[code];
        values[code] = pop && pop > 0 ? fighterCount / (pop / 1_000_000) : 0;
      } else {
        values[code] = fighterCount;
      }
    });
    return values;
  }, [fighterCounts, metric]);

  // Log-scale bin thresholds computed from the current data's positive range.
  const bins = useMemo(() => {
    const positive = Object.values(countryValues).filter(v => v > 0);
    if (positive.length === 0) return [];
    const min = Math.min(...positive);
    const max = Math.max(...positive);
    const logMin = Math.log10(min);
    const logMax = Math.log10(max);
    const step = (logMax - logMin) / POSITIVE_RAMP.length || 1;
    const thresholds = [];
    for (let i = 1; i < POSITIVE_RAMP.length; i++) {
      thresholds.push(Math.pow(10, logMin + step * i));
    }
    return thresholds; // length POSITIVE_RAMP.length - 1, ascending upper bounds
  }, [countryValues]);

  // -1 = unmapped/no geography match, 0 = zero, 1..POSITIVE_RAMP.length = positive bins.
  const getBinIndex = (value) => {
    if (value === undefined) return -1;
    if (value <= 0) return 0;
    let idx = bins.findIndex(threshold => value <= threshold);
    if (idx === -1) idx = POSITIVE_RAMP.length - 1;
    return idx + 1;
  };

  // Continuous fill color for a given value: 0 maps to t=0 (pure ZERO_COLOR),
  // the smallest positive value maps to t=1/(BIN_COLORS.length-1) (exactly
  // POSITIVE_RAMP[0]), and the max value maps to t=1 (the last ramp color) -
  // everything in between is a smooth log-scaled gradient, not a hard snap.
  const getColorForValue = useMemo(() => {
    const positive = Object.values(countryValues).filter(v => v > 0);
    if (positive.length === 0) {
      return (value) => value === undefined ? UNMAPPED_COLOR : ZERO_COLOR;
    }
    const min = Math.min(...positive);
    const max = Math.max(...positive);
    const logMin = Math.log10(min);
    const logMax = Math.log10(max);
    const zeroSlot = 1 / (BIN_COLORS.length - 1);
    return (value) => {
      if (value === undefined) return UNMAPPED_COLOR;
      if (value <= 0) return BIN_COLORS[0];
      const frac = logMax === logMin ? 1 : (Math.log10(value) - logMin) / (logMax - logMin);
      const t = zeroSlot + Math.min(Math.max(frac, 0), 1) * (1 - zeroSlot);
      return interpolateRamp(BIN_COLORS, t);
    };
  }, [countryValues]);

  const formatValue = (code) => {
    if (metric === 'perMillion') return countryValues[code].toFixed(2);
    return String(fighterCounts[code] || 0);
  };

  // Boundary values for the legend bar: edges[i] is the lower bound of bin i,
  // edges[edges.length - 1] is the upper bound of the last bin.
  const legendEdges = useMemo(() => {
    const positive = Object.values(countryValues).filter(v => v > 0);
    if (positive.length === 0) return null;
    const min = Math.min(...positive);
    const max = Math.max(...positive);
    return [0, min, ...bins, max];
  }, [bins, countryValues]);

  const fmt = (n) => metric === 'perMillion' ? n.toFixed(2) : Math.round(n).toLocaleString();

  // Dorling cartogram layout for Absolute mode: one circle per country with
  // fighters, area (not radius) proportional to fighter count - so r = k*sqrt(value)
  // with a single constant k, no min/max lerp that would distort the ratio between
  // circles. Settled via a force simulation that pulls each circle toward its true
  // geographic centroid while a collision force keeps them from overlapping.
  const dorlingNodes = useMemo(() => {
    if (metric !== 'fighters') return [];
    const raw = [];
    worldFeatures.forEach(f => {
      const code = numericToAlpha2[f.id];
      if (!code) return;
      const value = fighterCounts[code] || 0;
      if (value <= 0) return;
      const centroid = getMainLandmassCentroid(f.geometry);
      if (!Number.isFinite(centroid[0]) || !Number.isFinite(centroid[1])) return;
      raw.push({ code, value, x0: centroid[0], y0: centroid[1], x: centroid[0], y: centroid[1] });
    });
    if (raw.length === 0) return [];
    const maxValue = Math.max(...raw.map(n => n.value));
    // r = k*sqrt(value) with a single constant k -> area = pi*k^2*value is
    // exactly proportional to value for every circle, no floor/clamp that
    // would break that ratio for small values.
    const k = MAX_RADIUS / Math.sqrt(maxValue);
    raw.forEach(n => {
      n.r = k * Math.sqrt(n.value);
    });
    const sim = forceSimulation(raw)
      .force('x', forceX(n => n.x0).strength(0.9))
      .force('y', forceY(n => n.y0).strength(0.9))
      .force('collide', forceCollide(n => n.r + 1).iterations(3))
      .stop();
    for (let i = 0; i < 150; i++) sim.tick();
    return raw;
  }, [fighterCounts, metric]);

  const hasData = (rankings || []).length > 0;

  return (
    <div style={{ marginTop: '4em', paddingTop: '2em', borderTop: '1px solid #555' }}>
      <h1 style={{ textAlign: 'center', marginTop: '1.2em', marginBottom: '0.2em', fontWeight: 500, fontSize: '2.2em', color: '#fff' }}>World Map</h1>
      <div style={{ textAlign: 'center', marginBottom: '0.4em', fontSize: '1.15em', color: '#aaa', fontWeight: 500 }}>
        Same country breakdown as the table above, shown geographically.
      </div>
      {filterSummary && (
        <div style={{ textAlign: 'center', marginBottom: '1.6em', fontSize: '0.9em', color: '#888' }}>
          {filterSummary}
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '1.5em' }}>
        <div
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '0.5rem',
            padding: '0.35rem 0.5rem',
            cursor: 'pointer',
            userSelect: 'none',
          }}
          onClick={() => setMetric(m => m === 'fighters' ? 'perMillion' : 'fighters')}
        >
          <span style={{ fontSize: '0.8rem', color: '#ddd', fontWeight: 500, minWidth: '70px', textAlign: 'center' }}>Absolute</span>
          <div style={{ width: '32px', height: '20px', background: '#444', borderRadius: '10px', position: 'relative', border: '1px solid #555' }}>
            <div style={{
              width: '16px', height: '16px', background: '#fff', borderRadius: '50%', position: 'absolute',
              top: '2px', left: metric === 'fighters' ? '2px' : '14px', transition: 'all 0.25s ease',
            }} />
          </div>
          <span style={{ fontSize: '0.8rem', color: '#ddd', fontWeight: 500, minWidth: '70px', textAlign: 'center' }}>Per Million</span>
        </div>
      </div>

      <div
        ref={containerRef}
        style={{
          position: 'relative',
          maxWidth: '1000px',
          margin: '0 auto',
          background: '#1a1a1a',
          border: '1px solid #555',
          borderRadius: '4px',
          padding: '16px',
        }}
        onMouseMove={(e) => {
          if (!containerRef.current) return;
          const rect = containerRef.current.getBoundingClientRect();
          setCursorPos({ x: e.clientX - rect.left, y: e.clientY - rect.top });
        }}
      >
        {!hasData ? (
          <div style={{ color: '#ddd', textAlign: 'center', padding: '2em' }}>No data to display.</div>
        ) : (
          <ComposableMap
            projection="geoEqualEarth"
            projectionConfig={{ scale: MAP_SCALE }}
            width={MAP_WIDTH}
            height={MAP_HEIGHT}
            style={{ width: '100%', height: 'auto', display: 'block' }}
          >
            {metric === 'fighters' ? (
              <>
                <Geographies geography={worldTopo}>
                  {({ geographies }) =>
                    geographies.map(geo => (
                      <Geography
                        key={geo.rsmKey}
                        geography={geo}
                        fill="#3d3d3d"
                        stroke="#5a5a5a"
                        strokeWidth={0.5}
                        style={{ default: { outline: 'none' }, hover: { outline: 'none' }, pressed: { outline: 'none' } }}
                      />
                    ))
                  }
                </Geographies>
                {dorlingNodes.map(n => {
                  const binIndex = getBinIndex(n.value);
                  const opacity = hoveredBin === null || hoveredBin === binIndex ? 0.85 : DIMMED_OPACITY;
                  return (
                    <circle
                      key={n.code}
                      cx={n.x}
                      cy={n.y}
                      r={n.r}
                      fill={getColorForValue(n.value)}
                      fillOpacity={opacity}
                      stroke="#1a1a1a"
                      strokeWidth={1}
                      style={{ cursor: 'pointer', transition: 'fill-opacity 0.15s ease' }}
                      onMouseEnter={() => setHovered({ code: n.code, name: getCountryName(n.code === 'RU' ? 'RU-WHOLE' : n.code) })}
                      onMouseLeave={() => setHovered(null)}
                      onClick={() => onSelectCountry(n.code === 'RU' ? 'RU-WHOLE' : n.code)}
                    />
                  );
                })}
              </>
            ) : (
              <Geographies geography={worldTopo}>
                {({ geographies }) =>
                  geographies.map(geo => {
                    const code = numericToAlpha2[geo.id];
                    const value = code ? countryValues[code] : undefined;
                    const clickable = code !== undefined;
                    const binIndex = getBinIndex(value);
                    const opacity = hoveredBin === null || hoveredBin === binIndex ? 1 : DIMMED_OPACITY;
                    return (
                      <Geography
                        key={geo.rsmKey}
                        geography={geo}
                        fill={getColorForValue(value)}
                        fillOpacity={opacity}
                        stroke="#1a1a1a"
                        strokeWidth={0.5}
                        style={{
                          default: { outline: 'none', transition: 'fill-opacity 0.15s ease' },
                          hover: { outline: 'none', filter: clickable ? 'brightness(1.25)' : 'none' },
                          pressed: { outline: 'none' },
                        }}
                        onMouseEnter={() => {
                          if (clickable) setHovered({ code, name: getCountryName(code === 'RU' ? 'RU-WHOLE' : code) });
                        }}
                        onMouseLeave={() => setHovered(null)}
                        onClick={() => {
                          if (clickable) onSelectCountry(code === 'RU' ? 'RU-WHOLE' : code);
                        }}
                        cursor={clickable ? 'pointer' : 'default'}
                      />
                    );
                  })
                }
              </Geographies>
            )}
          </ComposableMap>
        )}

        {hovered && (
          <div
            style={{
              position: 'absolute',
              left: cursorPos.x + 12,
              top: cursorPos.y + 12,
              background: '#232323',
              border: '1px solid #555',
              borderRadius: '4px',
              padding: '6px 10px',
              fontSize: '0.85em',
              color: '#ddd',
              pointerEvents: 'none',
              whiteSpace: 'nowrap',
              zIndex: 10,
            }}
          >
            <b>{hovered.name}</b>: {formatValue(hovered.code)}
            {metric === 'perMillion'
              ? ` per million (${fighterCounts[hovered.code] || 0})`
              : ' fighters'}
          </div>
        )}
      </div>

      <div
        style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '10px', marginTop: '1.6em' }}
        onMouseLeave={() => setHoveredBin(null)}
      >
        <span style={{ fontSize: '0.8em', color: '#ccc' }}>No data</span>
        <div
          onMouseEnter={() => setHoveredBin(-1)}
          style={{
            width: '28px',
            height: '14px',
            borderRadius: '2px',
            border: '1px solid #666',
            cursor: 'pointer',
            opacity: hoveredBin === null || hoveredBin === -1 ? 1 : DIMMED_OPACITY,
            background: 'repeating-linear-gradient(45deg, #333, #333 3px, #454545 3px, #454545 6px)',
          }}
        />

        {legendEdges && (
          <div style={{ marginLeft: '14px' }}>
            <div style={{ position: 'relative', width: `${46 * BIN_COLORS.length}px`, height: '14px' }}>
              {/* Continuous gradient - same stops/positions as getColorForValue's interpolation */}
              <div
                style={{
                  position: 'absolute',
                  inset: 0,
                  borderRadius: '2px',
                  border: '1px solid #666',
                  boxSizing: 'border-box',
                  background: `linear-gradient(to right, ${BIN_COLORS.map((c, i) => `${c} ${(i / (BIN_COLORS.length - 1)) * 100}%`).join(', ')})`,
                }}
              />
              {/* Invisible hover zones (one per legacy bin) that dim the rest of the gradient */}
              <div style={{ position: 'absolute', inset: 0, display: 'flex' }}>
                {BIN_COLORS.map((_, i) => (
                  <div
                    key={i}
                    onMouseEnter={() => setHoveredBin(i)}
                    style={{
                      flex: 1,
                      cursor: 'pointer',
                      background: hoveredBin === null || hoveredBin === i ? 'transparent' : `rgba(26, 26, 26, ${1 - DIMMED_OPACITY})`,
                      transition: 'background 0.15s ease',
                    }}
                  />
                ))}
              </div>
            </div>
            <div style={{ position: 'relative', height: '16px', width: `${46 * BIN_COLORS.length}px` }}>
              {legendEdges.map((edge, i) => (
                <span
                  key={i}
                  style={{
                    position: 'absolute',
                    left: `${(i / (legendEdges.length - 1)) * 100}%`,
                    transform: i === 0 ? 'translateX(0)' : i === legendEdges.length - 1 ? 'translateX(-100%)' : 'translateX(-50%)',
                    fontSize: '0.72em',
                    color: '#999',
                    top: '2px',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {fmt(edge)}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default WorldRankingsMap;
